import * as XLSX from "xlsx";
import { toYmd } from "../ExpenseManagement/expenseSheetParser";

// EMP-06: reads the employee spreadsheet (CSV or Excel). Columns are matched by header text
// (case, spaces and punctuation ignored), never by position, against the column list the API
// publishes. Date columns are converted to YYYY-MM-DD here, so Excel date cells and
// "1 Sep 2026" text both work; everything else goes to the API as text and is validated there.
export const DATE_COLUMNS = ["date_of_joining", "date_of_birth"];
const norm = (v) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

export function parseEmployeeSheet(arrayBuffer, columns, maxRows = 500) {
  const wb = XLSX.read(arrayBuffer, { type: "array", cellDates: true, raw: true }) // raw: CSV text stays text ("0501" must not become 501);
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) throw new Error("The file has no sheets");
  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "", blankrows: false });
  if (!grid.length) throw new Error("The file is empty");

  const byNorm = new Map(columns.map((c) => [norm(c), c]));
  const headerIndex = grid[0].map((h) => byNorm.get(norm(h)) || null);
  if (!headerIndex.includes("full_name")) throw new Error("The first row must be the column headings, including full_name");

  const rows = [];
  for (const line of grid.slice(1)) {
    if (line.every(isBlank)) continue;
    const row = {};
    headerIndex.forEach((col, i) => {
      if (!col || isBlank(line[i])) return;
      if (DATE_COLUMNS.includes(col)) {
        const ymd = toYmd(line[i]);
        row[col] = ymd || String(line[i]).trim(); // an unreadable date is sent as typed so the preview names the row
      } else row[col] = typeof line[i] === "string" ? line[i].trim() : String(line[i]);
    });
    rows.push(row);
  }
  if (!rows.length) throw new Error("There are no employee rows below the headings");
  if (rows.length > maxRows) throw new Error(`Import at most ${maxRows} employees at a time (this file has ${rows.length})`);
  return rows;
}

// The blank template as CSV text: the headings and one example line.
export function templateCsv(columns) {
  const example = {
    employee_code: "",
    full_name: "Asha Rao",
    phone: "0501234567",
    email: "asha@example.com",
    role: "Support", // Admin, Support or Crew - required on every row
    date_of_joining: "2026-01-15",
    department: "Operations",
    designation: "Executive",
    employment_type: "PERMANENT",
    gender: "FEMALE",
  };
  const cell = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [columns.join(","), columns.map((c) => cell(example[c] || "")).join(",")].join("\n") + "\n";
}
